import styled from "@emotion/styled";
import DatetimeRange from "components-redesign/DatetimeInput/DatetimeRange";
import { FIGMA_COLORS, TYPOGRAPHY } from "components-redesign/styles";
import { COLORS_TO_BE_REVIEWED } from "components-redesign/styles/colors";
import LineitemStatusBadge from "components/Campaigns/components/LineitemStatusBadge";
import Tooltip from "components/Tooltip";
import CreativeStatusBadge from "components/Creatives/CreativeStatusBadge";
import { Datagrid } from "components/Datagrid/Datagrid";
import { DatagridColumn } from "components/Datagrid/types";
import { getIsoDateNow } from "helpers";
import { VIEW_STATUS } from "model/campaigns/viewStatus";
import { TimeRange } from "model/report/dateRange";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { getCampaignListRunStates } from "redux/campaignStatus/campaignStatusSelectors";
import { getAllCampaignsLineitemListRunStates } from "redux/campaignStatus/lineitemStatusSelectors";
import { getAllCampaignsObject } from "redux/campaigns/selectors";
import {
  getAllCreativesObject,
  getCreativesStatusObject,
} from "redux/creatives/selectors";
import { AttributeCell } from "./cells/AttributeCell";
import { ChangedByCell } from "./cells/ChangedByCell";
import { EntityCell } from "./cells/EntityCell";
import { TimeAndDateCell } from "./cells/TimeAndDateCell";
import { useChangeHistoryContext } from "./ChangeHistoryContext";
import { ChangeHistoryRow, TEntityType } from "./types";
import { getChangedByCount, getFieldNameCount } from "./utils";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;

  .MuiTableContainer-root {
    max-height: calc(100vh - 175px);
  }
`;

const DateRangeContainer = styled.div`
  padding: 24px;
  width: fit-content;
`;

const TableStyles = styled.div`
  flex: 1;
  overflow: auto;

  thead {
    background-color: transparent;
    position: sticky;
    top: 0;
    z-index: 100;
  }

  thead th {
    box-sizing: border-box;
    background-color: white;
    position: sticky;
    top: 0;
    border-top: 1px solid ${FIGMA_COLORS.surfaceContainerMonochromeLT};
    border-bottom: 1px solid ${FIGMA_COLORS.surfaceContainerMonochromeLT};
    border-right: 1px solid ${FIGMA_COLORS.surfaceContainerMonochromeLT};
  }

  tbody td {
    padding-top: 12px;
    padding-bottom: 12px;
    padding-right: 12px;
    padding-left: 12px;
    ${TYPOGRAPHY.bodyMedium}
    color: ${FIGMA_COLORS.onSurfaceVariantLight};
    border: none;
    vertical-align: top;
  }

  tbody tr {
    border-bottom: 1px solid ${FIGMA_COLORS.surfaceContainerMonochromeLT};
  }
`;

export const ChangeHistoryTable = () => {
  const {
    data,
    loading,
    dateRange,
    setDateRange,
    lineitemId,
    campaignId,
    creativeId,
  } = useChangeHistoryContext();
  const campaignsMap = useSelector(getAllCampaignsObject);
  const campaignRunStates = useSelector(getCampaignListRunStates);
  const lineitemRunStates = useSelector(getAllCampaignsLineitemListRunStates);
  const creativesStatusObject = useSelector(getCreativesStatusObject);
  const lineitemsMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const campaignId in campaignsMap) {
      const campaign = campaignsMap[campaignId];
      campaign.lineitems?.forEach((lineitem) => {
        if (lineitem.id) {
          map[lineitem.id] = lineitem.name;
        }
      });
    }
    return map;
  }, [campaignsMap]);
  const creativesObject = useSelector(getAllCreativesObject);
  const creativesMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const creativeId in creativesObject) {
      const creative = creativesObject[creativeId];
      if (creative?.name) {
        map[creativeId] = creative.name;
      }
    }
    return map;
  }, [creativesObject]);

  // Only show "Live all time" preset for lineitem and campaign contexts
  const presets = useMemo(() => {
    const basePresets = [
      TimeRange.today,
      TimeRange.yesterday,
      TimeRange.last7Days,
      TimeRange.lastWeek,
      TimeRange.last30Days,
      TimeRange.mtd,
      TimeRange.lastMonth,
    ];

    if (lineitemId || campaignId || creativeId) {
      return [TimeRange.allTime, ...basePresets];
    }

    return basePresets;
  }, [lineitemId, campaignId, creativeId]);

  const fieldNameOptions = useMemo(() => {
    const uniqueFields = new Set<string>();
    data.forEach((row) => {
      row.changes.forEach((change) => {
        uniqueFields.add(change.fieldName);
      });
    });
    return Array.from(uniqueFields).map((field) => ({
      value: field,
      count: getFieldNameCount(data, field),
    }));
  }, [data]);

  const uniqueEntities = useMemo(() => {
    const result: Record<string, TEntityType> = {};
    data.forEach((row) => {
      row.changes.forEach((change) => {
        result[change.entityId] = change.entityType;
      });
    });
    return result;
  }, [data]);

  const entityOnSearch = (value: string, row: ChangeHistoryRow) => {
    const entityId = value.split("|")[0];
    return row.changes.some((change) => change.entityId === entityId);
  };

  const campaignFilterOptions = useMemo(() => {
    return Object.keys(uniqueEntities)
      .filter((id) => uniqueEntities[id] === "Campaign")
      .map((entityId) => {
        const name = campaignsMap[entityId]?.name || entityId;
        const runState = campaignRunStates[entityId];
        const status = runState ? VIEW_STATUS[runState]?.status : undefined;
        return {
          value: `${entityId}|${name}`,
          renderLabel: () => (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "2px",
                overflow: "hidden",
                minWidth: 0,
              }}>
              <div
                style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <div style={{ flexShrink: 0, display: "flex" }}>
                  <LineitemStatusBadge
                    status={status}
                    statusLabel=""
                    statusTooltip={status ? `Currently ${status}` : undefined}
                  />
                </div>
                <span
                  style={{
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                  }}>
                  {name}
                </span>
              </div>
              <Tooltip
                title={entityId}
                placement="bottom-start">
                <span
                  style={{
                    color: COLORS_TO_BE_REVIEWED.neutral70,
                    fontSize: "12px",
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                  }}>
                  {entityId}
                </span>
              </Tooltip>
            </div>
          ),
        };
      });
  }, [uniqueEntities, campaignsMap, campaignRunStates]);

  const lineitemFilterOptions = useMemo(() => {
    return Object.keys(uniqueEntities)
      .filter((id) => uniqueEntities[id] === "Lineitem")
      .map((entityId) => {
        const name = lineitemsMap[entityId] || entityId;
        const status = lineitemRunStates[entityId]?.status;
        return {
          value: `${entityId}|${name}`,
          renderLabel: () => (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "2px",
                overflow: "hidden",
                minWidth: 0,
              }}>
              <div
                style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <div style={{ flexShrink: 0, display: "flex" }}>
                  <LineitemStatusBadge
                    status={status}
                    statusLabel=""
                    statusTooltip={status ? `Currently ${status}` : undefined}
                  />
                </div>
                <span
                  style={{
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                  }}>
                  {name}
                </span>
              </div>
              <Tooltip
                title={entityId}
                placement="bottom-start">
                <span
                  style={{
                    color: COLORS_TO_BE_REVIEWED.neutral70,
                    fontSize: "12px",
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                  }}>
                  {entityId}
                </span>
              </Tooltip>
            </div>
          ),
        };
      });
  }, [uniqueEntities, lineitemsMap, lineitemRunStates]);

  const creativeFilterOptions = useMemo(() => {
    return Object.keys(uniqueEntities)
      .filter((id) => uniqueEntities[id] === "Creative")
      .map((entityId) => {
        const name = creativesMap[entityId] || entityId;
        const status = creativesStatusObject[entityId]?.status;
        return {
          value: `${entityId}|${name}`,
          renderLabel: () => (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "2px",
                overflow: "hidden",
                minWidth: 0,
              }}>
              <div
                style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <div style={{ flexShrink: 0, display: "flex" }}>
                  <CreativeStatusBadge
                    status={status}
                    statusLabel=""
                    statusTooltip={status ? `Currently ${status}` : undefined}
                  />
                </div>
                <span
                  style={{
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                  }}>
                  {name}
                </span>
              </div>
              <Tooltip
                title={entityId}
                placement="bottom-start">
                <span
                  style={{
                    color: COLORS_TO_BE_REVIEWED.neutral70,
                    fontSize: "12px",
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                  }}>
                  {entityId}
                </span>
              </Tooltip>
            </div>
          ),
        };
      });
  }, [uniqueEntities, creativesMap, creativesStatusObject]);

  const changedByOptions = useMemo(() => {
    const uniqueUsers = new Set<string>();
    data.forEach((row) => {
      uniqueUsers.add(row.changedBy);
    });
    return Array.from(uniqueUsers).map((user) => ({
      value: user,
      count: getChangedByCount(data, user),
    }));
  }, [data]);

  const columns = useMemo<DatagridColumn[]>(
    () => [
      {
        field: "timestamp",
        headerName: "Time and date",
        align: "left",
        width: 170,
        sortable: true,
        renderCell: (row: ChangeHistoryRow) => <TimeAndDateCell row={row} />,
      },
      {
        field: "attribute",
        headerName: "Attribute",
        align: "left",
        width: 400,
        filterable: true,
        filterType: "dropdown",
        filterOptions: fieldNameOptions,
        onSearch: (filterValue: string | string[], row: ChangeHistoryRow) => {
          const filterArray = Array.isArray(filterValue)
            ? filterValue
            : filterValue
            ? [filterValue]
            : [];
          if (filterArray.length === 0) return true;
          return row.changes.some((change) =>
            filterArray.includes(change.fieldName)
          );
        },
        onSearchModify: (
          filterValue: string | string[],
          row: ChangeHistoryRow
        ) => {
          const filterArray = Array.isArray(filterValue)
            ? filterValue
            : filterValue
            ? [filterValue]
            : [];

          if (filterArray.length === 0) {
            return row;
          }
          //row arg here is a deep copy, so can modify in place
          row.changes = row.changes.filter((change) =>
            filterArray.includes(change.fieldName)
          );
          return row;
        },
        renderCell: (row: ChangeHistoryRow) => <AttributeCell row={row} />,
      },
      {
        field: "entity",
        headerName: "Entity",
        align: "left",
        width: 200,
        filterable: true,
        hideFilterCount: true,
        filterGroups: [
          {
            name: "Campaigns",
            field: "campaigns",
            options: campaignFilterOptions,
            onSearch: entityOnSearch,
          },
          {
            name: "Lineitems",
            field: "lineitems",
            options: lineitemFilterOptions,
            onSearch: entityOnSearch,
          },
          {
            name: "Creatives",
            field: "creatives",
            options: creativeFilterOptions,
            onSearch: entityOnSearch,
          },
        ].filter((g) => Array.isArray(g.options) && g.options.length > 0),
        renderCell: (row: ChangeHistoryRow) => <EntityCell row={row} />,
      },
      {
        field: "changedBy",
        headerName: "Changed by",
        align: "left",
        width: 150,
        filterable: true,
        filterType: "dropdown",
        filterOptions: changedByOptions,
        onSearch: (filterValue: string | string[], row: ChangeHistoryRow) => {
          const filterArray = Array.isArray(filterValue)
            ? filterValue
            : filterValue
            ? [filterValue]
            : [];
          if (filterArray.length === 0) return true;
          return filterArray.includes(row.changedBy);
        },
        renderCell: (row: ChangeHistoryRow) => <ChangedByCell row={row} />,
      },
    ],
    [
      fieldNameOptions,
      campaignFilterOptions,
      lineitemFilterOptions,
      creativeFilterOptions,
      changedByOptions,
    ]
  );

  return (
    <Container>
      <DateRangeContainer>
        <DatetimeRange
          value={dateRange}
          onChange={setDateRange}
          label=""
          hideTimePicker
          presets={presets}
          maxDate={getIsoDateNow()}
        />
      </DateRangeContainer>
      <TableStyles>
        <Datagrid
          data={data}
          columns={columns}
          loading={loading}
          stickyHeader
          tableContainerStyles={{
            height: "100%",
            maxHeight: "none",
          }}
          hasVirtualization
        />
      </TableStyles>
    </Container>
  );
};
